import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { parseSearchParams } from '@/utils/stringUtils';

/**
 * Extracts the organization slug from the URL pathname.
 * Supports routes like:
 * - /activate/:product/:slug
 * - /coverage/:product/:slug
 * - /coverage/:product/:slug/:careTeamUid/:token
 *
 * @param pathname - The URL pathname (e.g., '/coverage/sleepio/rtx/...')
 * @returns The slug if found, undefined otherwise
 */
const extractSlugFromPathname = (pathname: string): string | undefined => {
  const pathSegments = pathname.split('/').filter(Boolean);
  // Expected patterns: ['activate', 'sleepio', 'rtx'] or ['coverage', 'sleepio', 'rtx', ...]
  const validPrefixes = ['activate', 'coverage'];

  if (pathSegments.length >= 3 && validPrefixes.includes(pathSegments[0])) {
    return pathSegments[2]; // slug is always the third segment
  }

  return undefined;
};

/**
 * Captures org_slug from query params (?orgSlug=xxx) or URL path (/:product/:slug)
 * and registers it as a PostHog super property for session-wide event tracking.
 */
const OrgSlugCapture: React.FC = () => {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    const searchParams = parseSearchParams(location.search);

    // Priority 1: Query param (from Webflow) - supports both orgSlug and org_slug
    const orgSlugFromQuery = searchParams.orgSlug || searchParams.org_slug;

    // Priority 2: Path param (from CAP routes)
    const orgSlugFromPath = extractSlugFromPathname(location.pathname);

    const orgSlug = orgSlugFromQuery || orgSlugFromPath;

    if (!orgSlug) { return; }

    if (posthog) {
      posthog.register({ org_slug: orgSlug });
    }
  }, [location.search, location.pathname, posthog]);

  return null;
};

export default OrgSlugCapture;
